// Epic 79822 — PostIF list + approval service (the list-enrichment, mark-as-employee, and
// approver-review functions). Kept in its own module (separate from pifService.ts, which holds
// Epic 79820's form-wiring) so both epics' DTOs coexist without name/type collisions. pifService
// re-exports the symbols the list page and approval screens import.
import {
  PifApprovalAuditEntry,
  PifApprovalChainEntry,
  PifApprovalDisplayRow,
  PifApprovalRecord,
  PifApprovalRoleMeta,
  PifApproverRole,
  PifPositionMeta,
  PifApprovalStep,
  PifApprovalStepState,
  PifListItem,
  PifListFlow,
  PifListStatus,
  PifListEmploymentType,
  PIF_LIST_STATUS_ORDER,
} from "../pages/supervision/PostInterviewForm/types";
import {
  PostIfHrListResultDto,
  PostIfListItemDto,
  PostIfMyListResultDto,
  PostIfResultDto,
} from "../types/pifListDto";
import { PostIfLookupItem, PostIfPositionRules } from "../types/pifReferenceDto";
import { PostIfDto, PostIfSignatureDto } from "../types/pifApprovalDto";
import apiClient from "../utils/apiClient";
import { POSTIF_RESULT_CODE, readPostIfWrite } from "./pifWriteResult";
import { getCurrentESTTime, toDisplayDateOrNull } from "../utils/dateHelper";
import { PIF_FIELD_TYPE } from "../data/constants";
import { parsePifDate } from "../pages/supervision/PostInterviewForm/pifDates";
import { isBridgedFieldKey, PIF_APPROVAL_ROW_LABEL } from "../pages/supervision/PostInterviewForm/types";

/* ── PIF Approval — the screens' static role vocabulary (labels and CTAs).
 *
 * NOT stub data: the record itself is live, assembled in assemblePifApprovalRecord below from
 * GET /postif/{id} (header, applicant, answers and the signature timeline), with approve/reject
 * posting to /postif/approve and /postif/reject. Only the wording each role sees lives here.
 * The header this replaces still said "stub data, until the approval-chain API exists", which
 * outlived the wiring and read as if the whole screen were fake (PR 30856 review, Pooja Pujara). */

export const APPROVAL_ROLES: Record<PifApproverRole, PifApprovalRoleMeta> = {
  dept: {
    label: "Department Director",
    order: 2,
    signCta: "Sign and send to HR",
    // The stage the form moves to, not a person: no endpoint hands this screen the next signer's
    // name, and the invented names that sat here would have shipped on the receipt of every signed
    // form. Epic 82837 renamed the HR stage to the neutral "HR review" (was "HR Director").
    forward: "HR review",
    receiptNext: "HR review",
  },
  hr: {
    label: "HR Director",
    order: 3,
    signCta: "Approve and send to ED",
    forward: "Executive Director sign-off",
    receiptNext: "Executive Director sign-off",
  },
  exec: {
    label: "Executive Director",
    order: 4,
    signCta: "Sign - final approval",
    forward: null,
    receiptNext: "Onboarding and payroll setup",
  },
};

// API signature role → the approver role vocabulary the screens use (manager isn't an approver).
const API_ROLE_TO_APPROVER: Record<string, PifApproverRole> = {
  "dept-director": "dept",
  "department-director": "dept",
  "hr-director": "hr",
  "executive-director": "exec",
};

// Bug 82350: one spelling of each role for every workflow screen. 'manager' is the API's wire
// value for the creating supervisor, but "Manager (creator)" is not what the business calls that
// role — it reads as a different person from the "Supervisor" the preview and the done screen
// name. Keep the keys on the wire vocabulary and standardise only the display side.
const SIGNATURE_ROLE_LABEL: Record<string, string> = {
  manager: "Supervisor",
  "dept-director": "Department Director",
  "department-director": "Department Director",
  "hr-director": "HR Director",
  "executive-director": "Executive Director",
};

const APPROVAL_FLOW_TAG: Record<string, string> = {
  "new-hire": "New hire",
  "existing-active": "Internal transfer",
  "existing-inactive": "Rehire",
};

// Portal_PostIFApplicants.Status comes back lowercase from the DB (e.g. "active", "archived");
// the Applicant section displays it, so title-case it for read (Bug 82692).
const toTitleCase = (v: string): string => v.replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

// A calendar day (DOB, a date-type answer) arrives as "YYYY-MM-DD" or with a midnight time part.
// Read it as a local day via parsePifDate - new Date("YYYY-MM-DD") is UTC midnight, a day early in EST.
const calendarDayText = (v: string | null | undefined): string => {
  const date = parsePifDate((v ?? "").slice(0, 10));
  return date ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
};

const approvalStamp = (utc: string | null): string => {
  const date = toDisplayDateOrNull(utc);
  if (!date) return "";
  return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

/** Resolved id → display names the assembler can't get from the form read alone. */
export interface PifApprovalLookups {
  departmentName?: string;
  positionName?: string;
  rateTypeName?: string;
  reportsToName?: string;
  serviceName?: string;
  clientName?: string;
}

/** Pure assembler: turns the real form read (+ position rules + resolved names) into the rich
 * PifApprovalRecord the review screens render. Kept separate from the fetch so it is unit-testable. */
export const assemblePifApprovalRecord = (
  dto: PostIfDto,
  rules: PostIfPositionRules | null,
  currentUserId: number | null,
  lookups: PifApprovalLookups = {},
): PifApprovalRecord => {
  const { header, applicant } = dto;
  const answers = new Map(dto.answers.map((a) => [a.fieldKey, a.value ?? ""]));
  const answer = (key: string): string => answers.get(key) ?? "";

  const signatures = [...dto.signatures].sort((a, b) => (a.sortVal ?? 0) - (b.sortVal ?? 0));
  // A form is only awaiting signatures while its Status is 'submitted'. A rejection stamps ONLY
  // the rejecting row, so every downstream row is still Signed IS NULL — taking the earliest of
  // those unconditionally handed the NEXT director a live turn on a form that had just been
  // returned to its supervisor ('changes_requested') or declined outright. The chain is not
  // cleared and re-laid until the supervisor resubmits (sProc_Portal_PostIF_Submit_U), so until
  // then nobody's turn is live. sProc_Portal_PostIFSignatures_U enforces the same rule
  // server-side and answers 409, so this is the visible half of a two-sided gate, not the gate.
  //
  // With `pending` undefined everything downstream follows: no chain row is "current",
  // viewerIsCurrentSigner is false, the decision panel is replaced by the read-only note, and the
  // topbar chip reads "View only". The status itself stays visible in `status`.
  const statusKey = (header.status ?? "").trim().toLowerCase();
  const awaitingSignatures = statusKey === "submitted";
  const pending = awaitingSignatures ? signatures.find((s) => s.signed === null) : undefined;
  // Bug 82792: a decline and a send-back both persist as `signed = false` — POST /api/postif/reject
  // differs only by its `final` flag — so the signature row ALONE cannot tell them apart and the
  // chain used to call every decline "Returned". The form's status is the discriminator, exactly as
  // derivePifListStatus/derivePifApprovals already use it for the list screens.
  //
  // Reading it off the header is safe because at most one live `signed = false` row can exist per
  // form, and the status always describes that row: a resubmit soft-deletes the whole prior chain
  // including the rejected row before re-laying it (sProc_Portal_PostIF_Submit_U), and a declined
  // form can never be resubmitted at all (that proc accepts only 'draft'/'changes_requested').
  const isDeclined = statusKey === "declined";
  const currentRole: PifApproverRole =
    (pending?.role && API_ROLE_TO_APPROVER[pending.role.toLowerCase()]) || "dept";
  const viewerIsCurrentSigner = pending?.userId != null && pending.userId === currentUserId;
  // Creator/supervisor of the PIF. Only meaningful for resend while the chain is still at its first
  // stage (see viewerIsPreviousActor) — on its own it is NOT a grant.
  const viewerIsCreator = header.createdByUserId != null && header.createdByUserId === currentUserId;
  // Bug 84566: a form returned for changes is back with its creator to edit and resubmit.
  const viewerCanEditReturned = viewerIsCreator && statusKey === "changes_requested";

  // Bug 82334: "Resend sign link" belongs to whoever performed the step immediately BEFORE the
  // pending signer, and to nobody else. Walk back from the pending row: the nearest preceding
  // SIGNED row is the previous approver; if no signed row precedes it the chain is still at its
  // first stage, so the previous actor is the creator who submitted it.
  //
  // This deliberately narrows two older grants that were both permanent. The creator used to keep
  // resend for the whole life of the form, so a Manager could still email the HR Director after the
  // Dept Director had signed and forwarded past the Manager's stage (the reported bug); and "any
  // signed row is mine" handed it to every past approver at once, so in a dept → HR → exec chain
  // pending at exec the Dept Director kept it alongside the HR Director who actually forwarded.
  //
  // `pending` undefined (approved / declined / returned — see awaitingSignatures above) means there
  // is no signer to nudge, so nobody gets the action; the API answers WrongState (-4) for that call
  // anyway. A pending row never counts as its own predecessor: the current signer acts via the
  // decision panel, not resend.
  const pendingIndex = pending ? signatures.indexOf(pending) : -1;
  const previousApproval =
    pendingIndex > 0
      ? signatures
          .slice(0, pendingIndex)
          .reverse()
          .find((s) => s.signed === true)
      : undefined;
  const viewerIsPreviousActor =
    pending != null &&
    currentUserId != null &&
    (previousApproval ? previousApproval.userId === currentUserId : viewerIsCreator);

  const chain: PifApprovalChainEntry[] = signatures.map((s) => {
    const roleKey = (s.role ?? "").toLowerCase();
    const isPending = s === pending;
    // Bug 84702: the same per-state words as the list's approval chain (APPROVAL_STEP_DETAILS). A
    // rejected row is its own state rather than "done" (a green tick beside "Declined"), and every
    // row after a decline is "Not required" - nobody there will be asked to sign (Bug 83634).
    const state: PifApprovalChainEntry["state"] =
      s.signed === true
        ? "done"
        : s.signed === false
          ? isDeclined
            ? "declined"
            : "returned"
          : isPending
            ? "current"
            : isDeclined
              ? "not-required"
              : "waiting";
    // The supervisor's row is the submission, not an approval. Signed rows carry no reason on the
    // API side; a rejected row always does, and it is shown after the action.
    const detail =
      state === "done"
        ? roleKey === "manager"
          ? "Submitted & attested"
          : APPROVAL_STEP_DETAILS.signed
        : state === "declined" || state === "returned"
          ? `${APPROVAL_STEP_DETAILS[state]}${s.reason ? `: ${s.reason}` : ""}`
          : state === "current"
            ? APPROVAL_STEP_DETAILS.awaiting
            : APPROVAL_STEP_DETAILS[state === "not-required" ? "not-required" : "pending"];
    // Bug 82350: `name` used to be the role label too, so every chain row printed its role twice
    // and never said who the approver was. `role` is the heading, `name` is the person under it,
    // resolved server-side from Portal_Users by every signature read (sProc_Portal_PostIF_S,
    // sProc_Portal_PostIFSignatures_S, sProc_Portal_PostIFSignatures_History_S). A UserId that
    // doesn't resolve leaves `name` empty and the rail hides the line — never the role twice.
    return {
      name: `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim(),
      role: SIGNATURE_ROLE_LABEL[roleKey] ?? s.role ?? "Signer",
      detail,
      stamp: s.signed !== null ? approvalStamp(s.signedUtc) : "",
      state,
    };
  });

  // Epic 82838: the fifth lifecycle stage, so this screen shows the same five stages as the list's
  // Status column. It has no signature row - it is reached when HR marks the approved form as an
  // employee (Status = 'completed') - so it is appended here, and only to a chain that exists.
  if (chain.length > 0) {
    const completed = statusKey === "completed";
    chain.push({
      name: "",
      role: "Completed",
      detail: completed
        ? `Matched to employee${header.enterpriseEmployeeId ? ` (ID ${header.enterpriseEmployeeId})` : ""}`
        : APPROVAL_STEP_DETAILS[isDeclined ? "not-required" : "pending"],
      stamp: completed ? approvalStamp(header.markedAsEmployeeAt ?? null) : "",
      state: completed ? "done" : isDeclined ? "not-required" : "waiting",
    });
  }

  // Bug 82910: a resubmit soft-deletes the prior chain, so `signatures` holds only the latest cycle
  // and every earlier send-back/resubmit dropped off the trail. `signatureHistory` keeps them all.
  // Chronological, not SortVal: SortVal restarts at 1 in each cycle. PostIFSignatureId breaks ties
  // in insertion order, which is SortVal order within a cycle.
  const liveSignatureIds = new Set(signatures.map((s) => s.postIfSignatureId));
  const audit: PifApprovalAuditEntry[] = (dto.signatureHistory ?? signatures)
    .filter((s) => s.signed !== null)
    .sort((a, b) => (a.signedUtc ?? "").localeCompare(b.signedUtc ?? "") || a.postIfSignatureId - b.postIfSignatureId)
    .map((s) => {
      const label = SIGNATURE_ROLE_LABEL[(s.role ?? "").toLowerCase()] ?? s.role ?? "Signer";
      // Same Bug 82792 split as the chain detail above — the audit trail called a decline
      // "returned for changes" too, which read as the wrong action in a permanent record. Only the
      // live chain's rejection can be the decline: a declined form can never be resubmitted, so a
      // rejection from an earlier cycle was always a send-back.
      const declined = isDeclined && liveSignatureIds.has(s.postIfSignatureId);
      const action = s.signed ? "signed" : declined ? "declined" : "returned for changes";
      return { what: `${label} ${action}`, when: approvalStamp(s.signedUtc) };
    });

  // Epic 82838: conversion is the last event in a form's life, and it is not a signature, so the
  // trail above never had it - a Completed form's record stopped at "Executive Director signed".
  // Mark-as-employee is HR-only (CanAct 'mark') and only accepts an approved form, so this always
  // follows the last signature; it is appended rather than sorted in.
  if (statusKey === "completed") {
    audit.push({
      what: `HR marked as employee${header.enterpriseEmployeeId ? ` (ID ${header.enterpriseEmployeeId})` : ""}`,
      when: approvalStamp(header.markedAsEmployeeAt ?? null),
    });
  }

  const rateNum = parseFloat(answer("requested_rate"));
  const hasRate = Number.isFinite(rateNum);
  const meta = toPifPositionMeta(rules);
  const hiringMin = meta ? meta.hiring[0] : 0;
  const hiringMax = meta ? meta.hiring[1] : 0;
  const hasBand = !!meta && (hiringMin > 0 || hiringMax > 0);
  const outOfBand = hasRate && hasBand && (rateNum < hiringMin || rateNum > hiringMax);

  // Bug 82801: First Middle Last, as entered on the form. filter() drops a blank middle name so it
  // leaves no double space.
  const applicantName =
    [applicant?.firstName, applicant?.middleName, applicant?.lastName]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(" ") || "Applicant";
  const applicantInitials = `${applicant?.firstName?.[0] ?? ""}${applicant?.lastName?.[0] ?? ""}`.toUpperCase() || "+";
  const startDate = toDisplayDateOrNull(answer("desired_start_date"));
  const startDateText = startDate ? startDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const employeeId = header.enterpriseEmployeeId ?? applicant?.enterpriseEmployeeId ?? null;

  const dash = (v: string | null | undefined): string => (v && v.trim() ? v : "—");
  const listStatus = derivePifListStatus({
    status: header.status,
    latestSignatureRole: pending?.role,
    latestSignatureSigned: null,
  });
  const addressLine = [applicant?.address1, applicant?.city, applicant?.state, applicant?.zip].filter(Boolean).join(", ");
  // Bug 82671: render an actual asterisk mask, not the literal word "Masked" next to an empty
  // dash.
  //
  // Bug 82803: FULLY redacted, unconditionally - this is deliberately NOT `applicant.ssn`. That
  // value arrives pre-masked but with the last four visible ('***-**-####'), so passing it
  // through would show digits here the moment an SSN is actually on file. It never was until
  // Bug 82803 started persisting one for new applicants, which is exactly why this read looked
  // fully masked while being written to pass the last four through.
  //
  // The distinction that matters: on the form, the last four go back to the SUPERVISOR, who typed
  // them minutes earlier. This screen is the approval chain - a Department Director, HR Director
  // and Executive Director who never handled the number and do not act on it. They gain nothing
  // from the digits, so they do not get them.
  const ssnDisplay = "***-**-****";

  const rateRow: PifApprovalDisplayRow = { k: PIF_APPROVAL_ROW_LABEL.rate, v: hasRate ? `$${rateNum.toFixed(2)}` : "—" };
  if (hasRate && hasBand) {
    rateRow.pill = outOfBand ? "Outside hiring band" : "Inside band";
    rateRow.pillTone = outOfBand ? "warn" : "ok";
  }

  // Bug 82796: the driving answers - the Contact card's licence questions and the Pay card's
  // agency-vehicle question - were saved and read back on reopen, but never mapped here, so
  // approvers and the view-only screen never saw them. They show under the form's own condition
  // (the position may operate a vehicle), or whenever an answer is on file, since the rules read
  // can fail. Issuing state is not stored: the form shows a fixed, disabled "New York".
  //
  // Epic 82836 collapsed the two licence questions into one: "Will operate an agency vehicle?" = Yes
  // is what requires the licence number, and the form no longer writes has_license. So the licence
  // rows follow the vehicle answer. has_license is read only as a legacy answer - a form saved before
  // that change still carries it, and its row (and a "Yes" there) must keep showing.
  const yesNo = (v: string): string => (v === "yes" ? "Yes" : v === "no" ? "No" : "—");
  const hasLicense = answer("has_license");
  const willOperateVehicle = answer("will_operate_vehicle");
  const showDriving = !!rules?.mayOperateVehicle || !!hasLicense || !!willOperateVehicle;
  const showLicenseNumber = willOperateVehicle === "yes" || hasLicense === "yes";
  const licenseRows: PifApprovalDisplayRow[] = showDriving
    ? [
        ...(hasLicense ? [{ k: "Holds a driver's license", v: yesNo(hasLicense) }] : []),
        ...(showLicenseNumber
          ? [
              { k: "Issuing state", v: "New York" },
              { k: "License number", v: dash(answer("license_number")) },
            ]
          : []),
      ]
    : [];

  const compensationRows: PifApprovalDisplayRow[] = [
    rateRow,
    { k: "Rate type", v: dash(lookups.rateTypeName) },
    // Bug 82799: bridged answer, stored as the "Full-time"/"Part-time" label.
    { k: "Employment type", v: dash(answer("employment_type")) },
    { k: "Hours per week", v: dash(answer("hours_per_week")) },
    ...(showDriving ? [{ k: "Will operate an agency vehicle", v: yesNo(willOperateVehicle) }] : []),
    // Bug 82800: the supervisor's Pay-card note was saved and read back on reopen, but never
    // mapped here, so the approvers it is addressed to - and the view-only screen - never saw it.
    { k: "Notes for the approvers", v: dash(answer("department_notes")) },
  ];

  // Bug 82799: the form's "Additional details" card (PifSectionDynamic) - every catalogue field
  // that is not bridged into a bespoke control - had no counterpart here, so approvers never saw
  // those answers. Same filter, order and masking rule as the form.
  const additionalRows: PifApprovalDisplayRow[] = [...dto.answers]
    .filter((a) => !isBridgedFieldKey(a.fieldKey))
    .sort((a, b) => (a.sortVal ?? 0) - (b.sortVal ?? 0))
    .map((a) => ({
      k: a.header || a.fieldKey,
      v: a.isMasked
        ? (a.value ? "••••••" : "—")
        : a.dataType === PIF_FIELD_TYPE.DATE
          ? calendarDayText(a.value)
          : dash(a.value),
    }));

  const sections: PifApprovalRecord["sections"] = [
    {
      title: "Applicant",
      rows: [
        { k: "Full name", v: applicantName },
        // Bug 82799: both captured on the form's Applicant card but never shown to approvers.
        { k: "Known as", v: dash(applicant?.knownName) },
        { k: "Date of birth", v: calendarDayText(applicant?.dob) },
        { k: "Social Security Number", v: ssnDisplay },
        { k: "Employee ID", v: employeeId != null ? String(employeeId) : "New applicant" },
        // Bug 82795: the page header already carries the form's own status (record.status),
        // so an unqualified "Status" here reads as the PostIF's state rather than the person's.
        { k: "Applicant Status", v: toTitleCase(dash(applicant?.status)) },
      ],
    },
    {
      title: "Contact",
      rows: [
        { k: "Address", v: dash(addressLine) },
        { k: "Cell", v: dash(applicant?.cell) },
        { k: "Home", v: dash(applicant?.homePhone) },
        { k: "Email", v: dash(applicant?.email) },
        ...licenseRows,
      ],
    },
    {
      title: "Position and assignment",
      rows: [
        { k: PIF_APPROVAL_ROW_LABEL.department, v: dash(lookups.departmentName) },
        { k: PIF_APPROVAL_ROW_LABEL.position, v: dash(lookups.positionName) },
        ...(meta?.cls ? [{ k: "Classification", v: meta.cls }] : []),
        { k: "Reports to", v: dash(lookups.reportsToName) },
        ...(lookups.serviceName ? [{ k: "Service", v: lookups.serviceName }] : []),
        ...(lookups.clientName ? [{ k: "Individual / client", v: lookups.clientName }] : []),
        { k: PIF_APPROVAL_ROW_LABEL.startDate, v: startDateText },
      ],
    },
    {
      title: "Compensation and schedule",
      rows: compensationRows,
      ...(hasBand
        ? {
            band: {
              positionMin: meta ? meta.band[0] : 0,
              positionMax: meta ? meta.band[1] : 0,
              hiringMin,
              hiringMax,
              rate: hasRate ? rateNum : 0,
              outOfBand,
            },
          }
        : {}),
    },
    // Hidden when the catalogue has no generic fields, as the form's card is.
    ...(additionalRows.length ? [{ title: "Additional details", rows: additionalRows }] : []),
  ];

  return {
    postIfId: header.postIfId,
    pifId: header.refNo ?? `PIF-${header.postIfId}`,
    currentRole,
    applicantName,
    applicantInitials,
    positionTag: (header.flow && APPROVAL_FLOW_TAG[header.flow.toLowerCase()]) || "",
    applicantMeta: [lookups.positionName, lookups.departmentName, hasRate ? `$${rateNum.toFixed(2)}/hr` : null, `starts ${startDateText}`]
      .filter(Boolean)
      .join(" · "),
    // Bug 84702: the same stage the list shows - named and coloured by PostIFStatusChip - not the
    // raw "submitted", which said nothing about whose signature the form was waiting on.
    status: listStatus,
    employeeIdLabel: employeeId != null ? `Employee ID ${employeeId}` : "New applicant",
    viewerIsCurrentSigner,
    viewerIsPreviousActor,
    viewerCanEditReturned,
    chain,
    audit,
    sections,
  };
};

/** Who returned a form for changes, why, and when - the banner a supervisor needs on reopen. */
export interface PifReturnNote {
  by: string;
  reason: string;
  when: string;
}

/**
 * The rejection note for a form currently sitting in 'changes_requested'.
 *
 * Gated on the header status, not merely on "a rejected row exists": once the supervisor
 * re-submits, sProc_Portal_PostIF_Submit_U soft-deletes the whole prior chain, so a live payload
 * cannot carry a stale rejection - but keying off the status makes that intent explicit rather
 * than relying on the read to have filtered it. Highest sortVal wins; only one rejected row can
 * exist in a live chain, since a rejection halts it.
 *
 * Lives here beside the chain mapping so the role labels and the EST stamp have one source.
 *
 * Takes a structural parameter rather than a PostIfDto because this branch has two of them -
 * types/pifDto (what usePifDraft.load returns, no signatures declared) and types/pifApprovalDto
 * (what the approval screen reads). Both hit GET /postif/{id}, which does return the timeline, so
 * the field is present at runtime either way; declaring only what this needs works with both.
 */
export const derivePifReturnNote = (dto: {
  header: { status?: string | null };
  signatures?: PostIfSignatureDto[] | null;
}): PifReturnNote | null => {
  if ((dto.header.status ?? "").trim().toLowerCase() !== "changes_requested") return null;

  const returned = [...(dto.signatures ?? [])]
    .filter((s) => s.signed === false)
    .sort((a, b) => (b.sortVal ?? 0) - (a.sortVal ?? 0))[0];
  if (!returned) return null;

  return {
    by: SIGNATURE_ROLE_LABEL[(returned.role ?? "").toLowerCase()] ?? returned.role ?? "an approver",
    reason: (returned.reason ?? "").trim(),
    when: approvalStamp(returned.signedUtc),
  };
};

// Orchestrates the approver review: resolve the deep-link token (or numeric id) → load the form,
// its position rules, and the id→name lookups → assemble the record. Returns
// PIF_APPROVAL_NOT_RECIPIENT when resolve-token answers 403 (the link was sent to a different
// account than the one signed in; Bug 82783), before any record read. Returns null when the link
// can't be opened otherwise (expired/unknown token or the record isn't readable).
export const getPifApproval = async (
  lookupKey: string,
  currentUserId: number | null,
): Promise<PifApprovalRecord | typeof PIF_APPROVAL_NOT_RECIPIENT | null> => {
  const postIfId = /^\d+$/.test(lookupKey) ? Number(lookupKey) : await resolvePifApprovalToken(lookupKey);
  if (postIfId === PIF_APPROVAL_NOT_RECIPIENT) return postIfId;
  if (!postIfId) return null;

  let dto: PostIfDto;
  try {
    dto = await getPif(postIfId);
  } catch {
    return null;
  }

  const { header } = dto;
  const answerValue = (key: string): string | null => dto.answers.find((a) => a.fieldKey === key)?.value ?? null;
  const deptId = header.departmentId;
  const posId = header.deptPositionId;
  const serviceId = Number(answerValue("service"));
  const nameById = (list: PostIfLookupItem[], id: string | number | null): string | undefined =>
    id == null || id === "" ? undefined : list.find((x) => String(x.id) === String(id))?.text ?? undefined;

  const [departments, positions, rateTypes, reportToUsers, services, clients, rules] = await Promise.all([
    getPifDepartments().catch(() => []),
    deptId ? getPifPositions(deptId).catch(() => []) : Promise.resolve([]),
    getPifRateTypes().catch(() => []),
    // Not department-scoped: since Epic 82836 the form's Report To is picked from every active user,
    // so a person outside the hire's department must still resolve to a name here.
    getPifReportToUsers().catch(() => []),
    deptId ? getPifServices(deptId).catch(() => []) : Promise.resolve([]),
    Number.isFinite(serviceId) && serviceId > 0 ? getPifClients(serviceId).catch(() => []) : Promise.resolve([]),
    deptId && posId ? getPifPositionRules(deptId, posId).catch(() => null) : Promise.resolve(null),
  ]);

  return assemblePifApprovalRecord(dto, rules, currentUserId, {
    departmentName: nameById(departments, deptId),
    positionName: nameById(positions, posId),
    rateTypeName: nameById(rateTypes, answerValue("rate_type")),
    reportsToName: nameById(reportToUsers, answerValue("reports_to")),
    serviceName: nameById(services, answerValue("service")),
    clientName: nameById(clients, answerValue("client")),
  });
};

/* ── PIF list — real data via GET /api/postif/my-list and /hr-list (Epic 79822) ── */

// Same four roles as SIGNATURE_ROLE_LABEL above, in pipeline order. These abbreviated forms
// ("Dept Director", "Exec Director") were the other half of Bug 82350's inconsistency: the list
// dots and the detail dialog spelled the roles differently from the approval screens.
const APPROVAL_STEP_LABELS = ["Supervisor", "Department Director", "HR Director", "Executive Director"];

// Bug 84702: the same words the Signature History uses for each state (see PIF_CHAIN_DETAIL).
const APPROVAL_STEP_DETAILS: Record<PifApprovalStepState, string> = {
  signed: "Signed",
  awaiting: "Awaiting approval",
  // "Waiting", not "Pending", in the tooltip, the approval chain and Signature History - agreed
  // with the reporter on the work item.
  pending: "Waiting",
  declined: "Declined",
  returned: "Returned for changes",
  "not-required": "Not required",
};

// The supervisor's step is the submission, not an approval: "Submitted & attested" once the form is
// submitted, "Not submitted" while it is still a draft (Bug 84702).
const SUPERVISOR_STEP_DETAILS: Partial<Record<PifApprovalStepState, string>> = {
  signed: "Submitted & attested",
  pending: "Not submitted",
};

const chain = (states: PifApprovalStepState[]): PifApprovalStep[] =>
  APPROVAL_STEP_LABELS.map((label, index) => {
    const state = states[index] ?? "pending";
    const detail = (index === 0 && SUPERVISOR_STEP_DETAILS[state]) || APPROVAL_STEP_DETAILS[state];
    return { label, state, detail };
  });

// The API list rows are id-based; the display names (applicant / department / position /
// employment type) arrive via the pending list-DTO enrichment and stay blank until it lands.
// Signature staging (status chip + approval dots) is derived from `status` plus the row's current
// signature stage, which both list endpoints now return — the HR list only since Bug 79824, before
// which its rows had no stage at all and every submitted one rendered as awaiting the first
// signature. Still not the full per-role pipeline: only the stage the chain is sitting on is real,
// so the dots before it are inferred as signed rather than read row by row.

const FLOW_LABELS: Record<string, PifListFlow> = {
  "new-hire": "New",
  "existing-active": "Existing·Active",
  "existing-inactive": "Existing·Inactive",
};

// Portal_PostIFSignatures.Role → 0-based step in APPROVAL_STEP_LABELS.
const ROLE_STEP: Record<string, number> = {
  manager: 0,
  "dept-director": 1,
  "department-director": 1,
  "hr-director": 2,
  "executive-director": 3,
};

const mapFlow = (flow: string | null): PifListFlow => FLOW_LABELS[(flow ?? "").toLowerCase()] ?? "New";

// The 'employment_type' answer, as the list sprocs read it back: "Full-time"/"Part-time" from the
// form, "FT"/"PT" from the older grid, or "Full Time"/"Part Time" the way HR_EmploymentHistory
// spells it - all matched loosely, all reported in full words.
//
// Bug 82246: anything unrecognised used to default to "FT", so a row with NO answer (what the
// sprocs return until the form records one) was labelled full-time. An absent answer is now
// absent - the grid renders it as a dash instead of inventing a type.
const mapEmploymentType = (value?: string | null): PifListEmploymentType => {
  const v = (value ?? "").toLowerCase();
  if (v === "pt" || v.includes("part")) return "Part-time";
  if (v === "ft" || v.includes("full")) return "Full-time";
  return "";
};

const formatUpdated = (iso: string | null): string => {
  const date = toDisplayDateOrNull(iso);
  return date ? date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "";
};

/**
 * Epic 82838 (PR #32635 review): a row's "Last changed" as of now, for a successful write the page
 * applies locally instead of re-reading the list. Eastern and in the API's own naive format
 * (sProc_Portal_PostIF_AssignCoordinator_U stamps UpdatedUtc with fn_CurrentESTDateTime_S), so it
 * sorts against the other rows' server values.
 */
export const pifLastChangedNow = (): Pick<PifListItem, "updated" | "updatedUtc"> => {
  const now = getCurrentESTTime();
  return { updatedUtc: now, updated: formatUpdated(now) };
};

// The pending stage: the current signature stage, advanced by one when that stage is already signed.
const pendingStep = (role?: string | null, signed?: boolean | null): number => {
  const step = ROLE_STEP[(role ?? "").toLowerCase()];
  if (step === undefined) return 1; // submitted but the API sent no stage → first review stage
  return signed ? step + 1 : step;
};

// The stage that STOPPED the chain (declined outright, or returned for changes) — which is not the
// stage the chain is pending on, and must not be read off pendingStep directly (Bug 82789).
//
// Neither list sProc ever reports the rejecting row: sProc_Portal_PostIF_MyList_S and
// _HRList_S both pick `pending` as the earliest Signed IS NULL row and `lastSigned` as the latest
// Signed = 1 row, and a rejection is Signed = 0 — so it matches neither apply. A rejection stamps
// only the rejecter's own row and halts the chain, leaving every downstream row Signed IS NULL, so
// the role the sProc does report is always the one immediately AFTER the rejecter. Taking
// pendingStep at face value therefore blamed the next approver in the pipeline: an HR Director
// decline rendered as "HR Director: Signed · attested, Exec Director: Declined".
//
// The exception is the LAST role rejecting: no undecided row is left, the sProc falls back to
// lastSigned (Signed = 1) and pendingStep's own +1 already lands on the rejecter. `signed` being
// truthy is exactly that case, and is what tells the two apart.
const stoppedStep = (role?: string | null, signed?: boolean | null): number => {
  const step = pendingStep(role, signed);
  // No pending row — the fallback already advanced past the last signed role onto the rejecter.
  if (signed) return step;
  // No stage from the API at all: pendingStep's first-review-stage guess is as good as it gets,
  // and stepping back off it would blame the supervisor, who never rejects their own form.
  if (ROLE_STEP[(role ?? "").toLowerCase()] === undefined) return step;
  return Math.max(0, step - 1);
};

// Takes only the three fields it reads, so the approval screen's header can name the stage the same
// way from the form read (Bug 84702).
export const derivePifListStatus = (
  dto: Pick<PostIfListItemDto, "status" | "latestSignatureRole" | "latestSignatureSigned">,
): PifListStatus => {
  // The form's own status decides, never enterpriseEmployeeId or markedAsEmployeeAt. The employee
  // id is recorded at draft save for an existing employee, so it never meant "approved". And
  // conversion (sProc_Portal_PostIF_MarkAsEmployee_U) only accepts an 'approved' form, so a stamp
  // adds nothing an 'approved' status doesn't already say - yet QA holds submitted and draft rows
  // stamped by the Epic 79820 backfill. Trusting the stamp painted those APPROVED here and in the
  // approval-chain dialog while the form itself read "Submitted" (Bug 83111).
  const status = (dto.status ?? "").toLowerCase();
  if (status === "draft") return "DRAFT";
  // 'changes_requested' is an ACTIONABLE state, not a terminal one: sProc_Portal_PostIF_Submit_U
  // accepts it and the attribute/applicant IU procs still allow writes. Collapsing it into
  // DECLINED made the supervisor's own returned form look dead and disabled its Edit action.
  if (status === "changes_requested") return "RETURNED";
  if (status === "declined") return "DECLINED";
  // Fully signed off (Exec approved) but not yet marked-as-employee: enterpriseEmployeeId is still
  // null, so without this it fell through to the DRAFT fallback below — mislabelling an approved
  // hire as a draft and (since DRAFT is editable + isMine) letting the creator edit a locked form.
  if (status === "approved") return "APPROVED";
  // Epic 82838: mark-as-employee now sets Status='completed'. That is the converted state, distinct
  // from APPROVED, and it is still keyed off the status, not off markedAsEmployeeAt (see above).
  if (status === "completed") return "COMPLETED";
  if (status === "submitted") {
    const step = pendingStep(dto.latestSignatureRole, dto.latestSignatureSigned);
    if (step <= 1) return "PENDING DEPT DIR";
    if (step === 2) return "PENDING HR DIR";
    return "PENDING EXEC DIR";
  }
  return "DRAFT";
};

export const derivePifApprovals = (dto: PostIfListItemDto): PifApprovalStep[] => {
  const status = (dto.status ?? "").toLowerCase();
  // Same reason as derivePifListStatus above: the status, not the employee id or the conversion
  // stamp. Only an 'approved' form, or one converted from it ('completed', Epic 82838), has the
  // whole chain signed (Bug 83111).
  if (status === "approved" || status === "completed") return chain(["signed", "signed", "signed", "signed"]);
  if (status === "draft") return chain(["pending", "pending", "pending", "pending"]);
  if (status === "changes_requested" || status === "declined") {
    // Attribute the rejection to whoever actually made it. The old hardcoded second slot blamed
    // the Dept Director even when HR or the Executive Director was the rejecter; reading the
    // pending stage instead then blamed the approver one step PAST the rejecter (Bug 82789) —
    // stoppedStep maps that pending stage back onto the role that actually stopped the chain.
    //
    // The stopping stage carries its own state per status: a red "declined" dot on a returned form
    // reads as "this hire is over" when the supervisor can still fix and re-submit it, which is the
    // same distinction the RETURNED chip makes (PR 30856 review, Pooja Pujara).
    //
    // The same split holds for the stages after it (Bug 83634): a decline ends the form for good,
    // so nobody downstream will ever be asked to sign and "Pending" misread as someone still owing
    // an action. A returned form gets re-submitted through those stages, so they stay pending.
    const at = stoppedStep(dto.latestSignatureRole, dto.latestSignatureSigned);
    const isReturned = status === "changes_requested";
    const stopped: PifApprovalStepState = isReturned ? "returned" : "declined";
    const after: PifApprovalStepState = isReturned ? "pending" : "not-required";
    // Bug 84702: the stopping step carries the reason separately; each surface decides whether to show it.
    const reason = (dto.latestRejectionReason ?? "").trim();
    return chain(
      APPROVAL_STEP_LABELS.map<PifApprovalStepState>((_, index) =>
        index === at ? stopped : index < at ? "signed" : after,
      ),
    ).map((step, index) => (index === at && reason ? { ...step, reason } : step));
  }
  // submitted: manager (step 0) always signed; every stage before the pending one is signed too.
  // The pending stage itself is the one the form is waiting on (Bug 84702).
  const pending = pendingStep(dto.latestSignatureRole, dto.latestSignatureSigned);
  const states = APPROVAL_STEP_LABELS.map<PifApprovalStepState>((_, index) =>
    index === 0 || index < pending ? "signed" : index === pending ? "awaiting" : "pending",
  );
  return chain(states);
};

/**
 * Epic 82838 - the five lifecycle stages a row's Status column and the detail dialog show: the
 * four signatures (Submitted & attested = the supervisor's, then Dept Director, HR, Exec Director)
 * plus a fifth, Completed, signed once HR has matched the form to an employee.
 */
export const pifStatusPipeline = (row: PifListItem): PifApprovalStep[] => {
  const completed = row.status === "COMPLETED";
  // A declined form never reaches Completed, so it is "Not required" like every step after the
  // decline - not "Pending" (Bug 84702).
  const declined = row.status === "DECLINED";
  return [
    ...row.approvals,
    completed
      ? { label: "Completed", state: "signed", detail: "Matched to employee" }
      : declined
        ? { label: "Completed", state: "not-required", detail: APPROVAL_STEP_DETAILS["not-required"] }
        : { label: "Completed", state: "pending", detail: APPROVAL_STEP_DETAILS.pending },
  ];
};

const mapToPifListItem = (dto: PostIfListItemDto, currentUserId: number | null): PifListItem => ({
  id: String(dto.postIfId),
  title: dto.postIfTitle ?? dto.refNo ?? "",
  refNo: dto.refNo ?? undefined,
  flow: mapFlow(dto.flow),
  applicant: dto.applicantName ?? "",
  createdBy: dto.createdByName ?? "",
  createdByUserId: dto.createdByUserId ?? undefined,
  department: dto.departmentName ?? "",
  position: dto.positionName ?? "",
  employmentType: mapEmploymentType(dto.employmentType),
  status: derivePifListStatus(dto),
  updated: formatUpdated(dto.updatedUtc ?? dto.createdUtc),
  updatedUtc: dto.updatedUtc ?? dto.createdUtc ?? undefined,
  employeeNumber: dto.enterpriseEmployeeId ? `#${dto.enterpriseEmployeeId}` : undefined,
  enterpriseEmployeeId: dto.enterpriseEmployeeId ?? undefined,
  markedAsEmployee: !!dto.markedAsEmployeeAt,
  approvals: derivePifApprovals(dto),
  // "Mine" gates the edit/delete actions. The my-list also includes the caller's reports' forms,
  // so ownership is decided by creator id — not by which endpoint returned the row.
  isMine: dto.createdByUserId != null && dto.createdByUserId === currentUserId,
  hrCoordinatorUserId: dto.hrCoordinatorUserId ?? undefined,
  hrCoordinatorName: dto.hrCoordinatorName ?? undefined,
});

/**
 * Epic 82838: the HR list's default order, so HR works the backlog top-down - unassigned rows
 * first, then least recently changed first. Applied to the whole row set BEFORE the grid's scroll chunking; a grid sortModel
 * would only reorder the chunk already rendered. Rows with no timestamp go last. Returns a copy.
 */
export const sortPifHrBacklog = (rows: PifListItem[]): PifListItem[] =>
  [...rows].sort((a, b) => {
    // Unassigned rows first - the backlog HR still has to hand out (Epic 82838).
    const unassigned = Number(b.hrCoordinatorUserId === undefined) - Number(a.hrCoordinatorUserId === undefined);
    if (unassigned !== 0) return unassigned;
    if (!a.updatedUtc || !b.updatedUtc) return (a.updatedUtc ? 0 : 1) - (b.updatedUtc ? 0 : 1);
    return a.updatedUtc.localeCompare(b.updatedUtc);
  });

// The value a list column sorts on. Two columns display a formatted string that orders wrongly:
// "updated" ("Oct 2, 2026") sorts on its raw timestamp, "employeeNumber" ("#1234") on its number.
const pifSortValue = (row: PifListItem, field: string): unknown => {
  if (field === "updated") return row.updatedUtc;
  // The Status column sorts by lifecycle stage (1 -> 5, then Draft/Returned/Declined), not by label.
  if (field === "status") return PIF_LIST_STATUS_ORDER.indexOf(row.status);
  if (field === "employeeNumber") return row.enterpriseEmployeeId;
  return (row as unknown as Record<string, unknown>)[field];
};

/**
 * Sorts the WHOLE list on a column header click (Epic 82838). The grid reveals rows 25 at a time,
 * so letting DataGridPro sort only reordered the chunk already rendered. Blank values go last in
 * either direction. Returns a copy.
 */
export const sortPifListRows = (rows: PifListItem[], field: string, direction: "asc" | "desc"): PifListItem[] => {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = pifSortValue(a, field);
    const right = pifSortValue(b, field);
    const leftBlank = left === undefined || left === null || left === "";
    const rightBlank = right === undefined || right === null || right === "";
    if (leftBlank || rightBlank) return Number(leftBlank) - Number(rightBlank);
    if (typeof left === "number" && typeof right === "number") return (left - right) * sign;
    return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" }) * sign;
  });
};

// Both lists fetch one large page and the component filters it client-side. When the HR list fills
// that page there may be more rows than were loaded, and only then does the component hand its
// search term to the server instead (Epic 82838: "server term for large sets, client filter otherwise").
export const HR_LIST_PAGE_SIZE = 200;

export const getMyPostInterviewForms = async (currentUserId: number | null): Promise<PifListItem[]> => {
  // Bug 79824: my-list now answers { rows, total } rather than a bare array - reading it as an
  // array crashed the page outright ("...map is not a function"). It is asked for one large page
  // here on purpose: the list component still filters and searches the rows it holds, so a real
  // page size would silently narrow those filters to page 1. Same interim shape as the HR list
  // above; both switch to true server paging when the component does.
  const response = await apiClient.get<PostIfMyListResultDto>("/postif/my-list", {
    params: { pageNumber: 1, pageSize: HR_LIST_PAGE_SIZE },
  });
  return (response.data?.rows ?? []).map((row) => mapToPifListItem(row, currentUserId));
};

/**
 * `search` is sent only when given: the server matches title, creator, applicant name and Employee
 * ID (sProc_Portal_PostIF_HRList_S, Epic 82838), never SSN.
 */
export const getPostInterviewForms = async (
  currentUserId: number | null,
  filters: PifHrListServerFilters = {},
): Promise<PifListItem[]> => {
  // Only the filters actually set go on the query string - an absent one means "no filter".
  const extra = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== ""),
  );
  const response = await apiClient.get<PostIfHrListResultDto>("/postif/hr-list", {
    params: { pageNumber: 1, pageSize: HR_LIST_PAGE_SIZE, ...extra },
  });
  return (response.data?.rows ?? []).map((row) => mapToPifListItem(row, currentUserId));
};

/**
 * Epic 82838 (PR #32636 / #32635 review): the HR list filters the server applies once the list
 * outgrows one page - the page would otherwise filter and sort only the 200 rows it loaded.
 */
export interface PifHrListServerFilters {
  search?: string;
  /** Portal_PostIF.Status as the API stores it - see toPifApiStatus. */
  status?: string;
  departmentId?: number;
  unassigned?: boolean;
  hrCoordinatorUserId?: number;
}

/**
 * A list status as the server filters it. The three pending stages are all Status = 'submitted'
 * (the stage is a signature-chain fact, not a column), so the page narrows those to the exact
 * stage itself after the server has filtered to 'submitted'.
 */
export const toPifApiStatus = (status: PifListStatus): string => {
  switch (status) {
    case "PENDING DEPT DIR":
    case "PENDING HR DIR":
    case "PENDING EXEC DIR":
      return "submitted";
    case "RETURNED":
      return "changes_requested";
    default:
      return status.toLowerCase(); // draft | approved | completed | declined
  }
};

/** Epic 82838 - the HR Coordinator picker: active members of the HR role as {id, text}. */
export const getPostIfHrCoordinators = async (): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/hr-coordinators");
  return response.data ?? [];
};

/**
 * Epic 82838 - assigns a coordinator, or clears it with `hrCoordinatorUserId: null`. HR-only on
 * the server (403); an assignee outside the pool answers 422. Last write wins.
 *
 * Bug 84560: that 422 (-2) RESOLVES as `{ code: -2 }` rather than throwing. On this endpoint -2
 * means only "no longer an HR Coordinator" - the picker went stale since the page loaded - so the
 * caller says so and reloads the picker, instead of the shared "refused one of the answers" alert.
 */
export const assignPostIfCoordinator = (params: {
  postIfId: number;
  hrCoordinatorUserId: number | null;
}): Promise<PostIfResultDto> =>
  readPostIfWrite(
    () =>
      apiClient.post<PostIfResultDto>("/postif/assign-coordinator", {
        postIfId: params.postIfId,
        hrCoordinatorUserId: params.hrCoordinatorUserId,
      }),
    [POSTIF_RESULT_CODE.VALIDATION_FAILED],
  );

/* ── PIF list row actions (Epic 79822) ── */

// Soft-deletes a draft (creator-only; a submitted form is never deletable — enforced server-side).
export const softDeletePostIf = (postIfId: number): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/soft-delete", { postIfId }));

// HR converts an approved PostIF into an employee record by linking its EnterpriseEmployeeId.
export const markPostIfAsEmployee = (params: {
  postIfId: number;
  enterpriseEmployeeId: number;
}): Promise<PostIfResultDto> =>
  readPostIfWrite(() =>
    apiClient.post<PostIfResultDto>("/postif/mark-as-employee", {
      postIfId: params.postIfId,
      enterpriseEmployeeId: params.enterpriseEmployeeId,
    }),
  );

/* ── PIF form reference data / lookups (Epic 79818) ── */
// The 7 dropdowns share the flat {id, text} shape; position-rules returns the band/classification
// metadata for a chosen department + position. All are GET, JWT-scoped (no nonce). These back the
// form's cascading selects; the component wiring that consumes them lands in the next sub-slice.

export const getPifDepartments = async (): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/departments");
  return response.data ?? [];
};

export const getPifPositions = async (departmentId: number): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/positions", {
    params: { departmentId },
  });
  return response.data ?? [];
};

// Every active Enterprise user - the same list the form's Report To is picked from (Epic 82836).
export const getPifReportToUsers = async (): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/report-to-users");
  return response.data ?? [];
};

export const getPifSiteLocations = async (): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/site-locations");
  return response.data ?? [];
};

export const getPifServices = async (departmentId: number): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/services", {
    params: { departmentId },
  });
  return response.data ?? [];
};

export const getPifClients = async (serviceId: number): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/clients", {
    params: { serviceId },
  });
  return response.data ?? [];
};

export const getPifRateTypes = async (): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/rate-types");
  return response.data ?? [];
};

export const getPifPositionRules = async (
  departmentId: number,
  positionId: number,
): Promise<PostIfPositionRules> => {
  const response = await apiClient.get<PostIfPositionRules>("/postif/position-rules", {
    params: { departmentId, positionId },
  });
  return response.data;
};

// Adapts the API's position-rules to the form's internal `meta` shape, so the validation, pay-band,
// and offer-rail logic keeps reading `meta.cls/office/sched/band/hiring` unchanged. Null band
// bounds (no rate seeded yet) collapse to 0 — the pay band just shows no range for that position.
export const toPifPositionMeta = (rules: PostIfPositionRules | null): PifPositionMeta | null =>
  rules
    ? {
        cls: rules.classification ?? "",
        office: rules.isOffice,
        sched: rules.isScheduleBased,
        band: [rules.positionMin ?? 0, rules.positionMax ?? 0],
        hiring: [rules.hiringMin ?? 0, rules.hiringMax ?? 0],
      }
    : null;

/* ── PIF detail read + approval actions (Epic 79821 GET + Epic 79823) ── */

// GET /api/postif/{id} — the full form (header + applicant + EAV catalogue + signature timeline).
// Used to load a draft for editing and to build the approver's read-only review.
export const getPif = async (postIfId: number): Promise<PostIfDto> => {
  const response = await apiClient.get<PostIfDto>(`/postif/${postIfId}`);
  return response.data;
};

// GET /api/postif/approval-state — the signature timeline on its own (a lighter read than getPif).
export const getPifApprovalState = async (postIfId: number): Promise<PostIfSignatureDto[]> => {
  const response = await apiClient.get<PostIfSignatureDto[]>("/postif/approval-state", {
    params: { postIfId },
  });
  return response.data ?? [];
};

// Bug 82783: resolve-token answered 403 - the link was sent to a different account than the one
// signed in, so the approval screen asks the viewer to sign in as that account instead.
export const PIF_APPROVAL_NOT_RECIPIENT = "not-recipient" as const;

// Resolves an emailed approval deep-link token to its numeric PostIF id. 404 (unknown) and 410
// (expired) both collapse to null — the approval screen treats either as "this link can't be opened".
export const resolvePifApprovalToken = async (
  token: string,
): Promise<number | typeof PIF_APPROVAL_NOT_RECIPIENT | null> => {
  try {
    const response = await apiClient.get<{ postIfId: number }>(`/postif/resolve-token/${encodeURIComponent(token)}`);
    return response.data?.postIfId ?? null;
  } catch (error) {
    // Read structurally, not through axios.isAxiosError — see pifWriteResult.ts for why.
    return (error as { response?: { status?: number } })?.response?.status === 403 ? PIF_APPROVAL_NOT_RECIPIENT : null;
  }
};

// POST /api/postif/approve — the current signer approves and advances the chain.
export const approvePifById = (postIfId: number): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/approve", { postIfId }));

// POST /api/postif/reject — final=false returns the form for changes (re-submittable); final=true
// is a terminal decline.
export const rejectPifById = (params: {
  postIfId: number;
  reason: string;
  final: boolean;
}): Promise<PostIfResultDto> =>
  readPostIfWrite(() =>
    apiClient.post<PostIfResultDto>("/postif/reject", {
      postIfId: params.postIfId,
      reason: params.reason,
      final: params.final,
    }),
  );

// POST /api/postif/resend-approval-link — re-emails a fresh sign-link to the current pending
// approver. The creator/supervisor's action from the read-only review; returns the standard result.
export const resendPifApprovalLink = (postIfId: number): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/resend-approval-link", { postIfId }));

// POST /api/postif/email-hr-director — the supervisor asking HR for go-ahead BEFORE any PostIF
// exists, so there is no id to send and the body is empty. The recipient is resolved server-side
// from the "HR Director User Id" setting; 409 means no director is configured (or has no email on
// file), which useApi surfaces as an error alert.
export const emailHrDirectorApprovalRequest = (): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/email-hr-director", {}));

// POST /api/postif/email-hr-do-not-rehire — the supervisor asking HR how to proceed after the
// identity lookup blocked someone as Do Not Rehire (Bug 82168). Same server-side path as
// email-hr-director above (recipient resolved from the "HR Director User Id" setting, same rate
// limit), but a Do-Not-Rehire-specific template, so it needs the employee id the mail identifies
// the person by. A separate endpoint rather than a parameter on email-hr-director, which is
// documented as taking no body. Deliberately sends only the employee id — never the SSN or the raw
// lookup value the supervisor typed.
export const emailHrDoNotRehireContact = (employeeId: number): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/email-hr-do-not-rehire", { employeeId }));
